import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'

/**
 * Authentication Store - Manages user authentication and JWT tokens
 * Handles login, logout, and authentication state management
 */
export const useAuthStore = defineStore('auth', () => {
  // State
  const isAuthenticated = ref(false)
  const authEnabled = ref(false)
  const user = ref(null)
  const token = ref(null)
  const isLoading = ref(false)
  const error = ref(null)

  // Computed
  const needsAuth = computed(() => authEnabled.value && !isAuthenticated.value)

  /**
   * Initialize authentication state from localStorage
   */
  async function initializeAuth() {
    try {
      // Load stored token and user info
      const storedToken = localStorage.getItem('iot-auth-token')
      const storedUser = localStorage.getItem('iot-auth-user')

      if (storedToken && storedUser) {
        token.value = storedToken
        user.value = JSON.parse(storedUser)
        isAuthenticated.value = true
        
        // Set axios default header for all requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`
      }

      // Check auth status with backend and wait for completion
      await checkAuthStatus()
    } catch (error) {
      console.error('Error initializing auth:', error)
      clearAuthData()
    }
  }

  /**
   * Check authentication status with backend
   */
  async function checkAuthStatus() {
    try {
      const response = await axios.get('/api/auth/status')
      authEnabled.value = response.data.authEnabled

      if (!authEnabled.value) {
        // Authentication is disabled on backend
        isAuthenticated.value = true
        return
      }

      // If we have a token, verify it with backend
      if (token.value) {
        await verifyToken()
      }
    } catch (error) {
      console.error('Error checking auth status:', error)
      if (authEnabled.value) {
        clearAuthData()
      }
    }
  }

  /**
   * Verify current token with backend
   */
  async function verifyToken() {
    try {
      const response = await axios.get('/api/auth/verify')
      
      if (response.data.valid) {
        isAuthenticated.value = true
        if (response.data.user) {
          user.value = response.data.user
          localStorage.setItem('iot-auth-user', JSON.stringify(response.data.user))
        }
      } else {
        clearAuthData()
      }
    } catch (error) {
      console.error('Token verification failed:', error)
      clearAuthData()
    }
  }

  /**
   * Login with username and password
   */
  async function login(username, password) {
    try {
      isLoading.value = true
      error.value = null

      const response = await axios.post('/api/auth/login', {
        username,
        password
      })

      const { token: newToken, user: newUser, authEnabled: backendAuthEnabled } = response.data

      authEnabled.value = backendAuthEnabled

      if (!backendAuthEnabled) {
        // Auth is disabled, consider user logged in
        isAuthenticated.value = true
        return { success: true, authDisabled: true }
      }

      // Store authentication data
      token.value = newToken
      user.value = newUser
      isAuthenticated.value = true

      // Persist to localStorage
      localStorage.setItem('iot-auth-token', newToken)
      localStorage.setItem('iot-auth-user', JSON.stringify(newUser))

      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

      // Clear any previous errors
      error.value = null

      return { success: true }

    } catch (error) {
      console.error('Login error:', error)
      error.value = error.response?.data?.error || 'Login failed'
      return { 
        success: false, 
        error: error.value 
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Logout user and clear authentication data
   */
  async function logout() {
    try {
      isLoading.value = true
      // Notify backend (optional)
      await axios.post('/api/auth/logout').catch(() => {
        // Ignore errors, logout locally anyway
      })
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearAuthData()
      isLoading.value = false
    }
  }

  /**
   * Clear all authentication data
   */
  function clearAuthData() {
    isAuthenticated.value = false
    user.value = null
    token.value = null
    // Don't clear error here - let it be managed by the calling context

    // Clear localStorage
    localStorage.removeItem('iot-auth-token')
    localStorage.removeItem('iot-auth-user')

    // Remove axios default header
    delete axios.defaults.headers.common['Authorization']
  }

  /**
   * Get current authentication token for socket connections
   */
  function getSocketAuthToken() {
    return token.value
  }

  /**
   * Handle authentication errors (401/403)
   */
  function handleAuthError() {
    if (authEnabled.value) {
      const hadToken = token.value !== null
      clearAuthData()
      // Only show "session expired" if we actually had a token before
      if (hadToken) {
        error.value = 'Session expired. Please log in again.'
      }
    }
  }

  // Setup axios interceptor to handle auth errors
  axios.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401 && authEnabled.value) {
        handleAuthError()
      }
      return Promise.reject(error)
    }
  )

  return {
    // State
    isAuthenticated,
    authEnabled,
    user,
    token,
    isLoading,
    error,
    
    // Computed
    needsAuth,
    
    // Actions
    initializeAuth,
    checkAuthStatus,
    login,
    logout,
    clearAuthData,
    getSocketAuthToken,
    handleAuthError,
    verifyToken
  }
})